import { Commit } from '@basehub/data/replicache/index'
import * as React from 'react'
import { create } from 'zustand'
import { useDashboardSlugs } from '~/hooks/use-dashboard-slugs'
import { useSelectedBranchOrCommitQuery } from '~/lib/commits/use-selected-branch-or-commit'

interface CommitNavigationState {
  currentCommitId: string | null
  currentCommit: Commit | null
  previousCommitId: string | null
  previousCommit: Commit | null
  isCheckingOutThisCommit: boolean
  currentHref: string
  previousHref: string | null
}

export const useCommitNavigationStore = create<CommitNavigationState>(() => ({
  currentCommitId: null,
  currentCommit: null,
  previousCommitId: null,
  previousCommit: null,
  isCheckingOutThisCommit: false,
  currentHref: '',
  previousHref: '',
}))

interface CommitNavigationProviderProps {
  children: React.ReactNode
  currentCommit: Commit | null
  previousCommit: Commit | null
  activeBlockId: string | null
}

export const CommitNavigationProvider: React.FC<
  CommitNavigationProviderProps
> = ({ children, currentCommit, activeBlockId, previousCommit }) => {
  const { teamSlug, repoSlug, repoPage } = useDashboardSlugs()
  const { data: selectedBranchOrCommit } = useSelectedBranchOrCommitQuery()

  const isCheckingOutThisCommit =
    selectedBranchOrCommit?.id === currentCommit?.id
  const currentHref = React.useMemo(
    () =>
      `/${teamSlug}/${repoSlug}/${repoPage}/${encodeURIComponent(
        currentCommit?.id ?? ''
      )}${activeBlockId ? `/${activeBlockId}` : ''}`,
    [activeBlockId, currentCommit?.id, repoPage, repoSlug, teamSlug]
  )

  const previousHref = React.useMemo(
    () =>
      previousCommit?.id
        ? `/${teamSlug}/${repoSlug}/${repoPage}/${encodeURIComponent(
            previousCommit?.id
          )}${activeBlockId ? `/${activeBlockId}` : ''}`
        : null,
    [activeBlockId, previousCommit?.id, repoPage, repoSlug, teamSlug]
  )

  React.useEffect(() => {
    useCommitNavigationStore.setState({
      currentCommit,
      previousCommit,
      currentCommitId: currentCommit?.id ?? null,
      previousCommitId: previousCommit?.id ?? null,
      isCheckingOutThisCommit,
      currentHref,
      previousHref,
    })
  }, [
    currentCommit,
    previousCommit,
    isCheckingOutThisCommit,
    currentHref,
    previousHref,
  ])

  return <>{children}</>
}
